import { Component, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { LeaseDetailService } from '../../data/services/view/lease-detail.service';
import { ApiLeaseService } from '../../data/services/api/api-lease.service';
import { MessageService } from 'primeng/api';
import { conExtensionDe, guardarBlobComo, nombreLegibleDeUrl } from '@data/utils/descarga-archivo.util';

type ContractDocField = 'contractAppoval' | 'promissoryApproval' | 'invoice';

@Component({
  selector: 'app-lease-agreement',
  templateUrl: './lease-agreement.component.html',
  styleUrls: ['./lease-agreement.component.scss'],
})
export class LeaseAgreementComponent implements OnInit {
  /** Formulario del número de contrato (capturado por legal). */
  contractForm: FormGroup;
  savingNumber = false;
  /** Tasa moratoria mensual del arrendamiento, capturada en % con motivo obligatorio. */
  rateForm: FormGroup;
  savingRate = false;

  constructor(
    public leaseDetailService: LeaseDetailService,
    private apiLeaseService: ApiLeaseService,
    private messageService: MessageService,
    private fb: FormBuilder,
  ) {
    this.contractForm = this.fb.group({ contractNumber: [''] });
    this.rateForm = this.fb.group({
      ratePercent: [null, [Validators.required, Validators.min(0), Validators.max(20)]],
      reason: ['', [Validators.required, Validators.maxLength(500)]],
    });
  }

  ngOnInit(): void {
    // El tabView se monta dentro de *ngIf="data", así que aquí `data` ya está cargada.
    this.contractForm.patchValue({ contractNumber: this.data?.contract?.contractNumber ?? '' });
    this.rateForm.patchValue({ ratePercent: this.data?.moratoryRate != null ? Number((this.data.moratoryRate * 100).toFixed(2)) : null });
  }

  get data() {
    return this.leaseDetailService.data
  }

  /**
   * Normaliza un campo de documento a string[]. Tolera leases legacy donde el
   * campo aún es un string único (antes de correr la migración a arrays).
   */
  toArray(value: any): string[] {
    return Array.isArray(value) ? value : (value ? [value] : []);
  }

  /**
   * Persiste el array completo de una sección de documentos (lo emite
   * `app-input-multi-file` tras subir/borrar). Preserva los otros 2 arrays + el
   * número de contrato (el backend reemplaza el sub-doc `contract` completo).
   */
  onDocsChanged(field: ContractDocField, urls: string[]): void {
    const current = this.data?.contract ?? {};
    const payload = {
      contract: {
        contractAppoval: this.toArray(current.contractAppoval),
        promissoryApproval: this.toArray(current.promissoryApproval),
        invoice: this.toArray(current.invoice),
        contractNumber: current.contractNumber ?? '',
        [field]: urls,
      }
    };
    this.saveContract(payload, 'Documentos actualizados correctamente.');
  }

  /**
   * Guarda el número de contrato preservando los 3 arrays de documentos del
   * sub-doc `contract`.
   */
  saveContractNumber(): void {
    const current = this.data?.contract ?? {};
    const payload = {
      contract: {
        contractAppoval: this.toArray(current.contractAppoval),
        promissoryApproval: this.toArray(current.promissoryApproval),
        invoice: this.toArray(current.invoice),
        contractNumber: (this.contractForm.value.contractNumber ?? '').trim(),
      }
    };

    this.savingNumber = true;
    this.apiLeaseService.putCollection(payload, undefined, 'leases/' + this.leaseDetailService.id)
      .subscribe((response) => {
        this.savingNumber = false;
        if (!response.error) {
          this.leaseDetailService.getLeaseDetail();
          this.messageService.add({
            severity: 'success',
            summary: 'Éxito',
            detail: 'Número de contrato guardado correctamente.'
          });
        } else {
          this.messageService.add({
            severity: 'error',
            summary: 'Error',
            detail: response.msg || 'No se pudo guardar el número de contrato.'
          });
        }
      });
  }

  /**
   * Manejar descarga de archivos.
   *
   * `etiquetaSeccion` NO puede ser el nombre del archivo: estas tres secciones
   * admiten N archivos cada una, así que nombrarlos a todos "factura_compra.pdf"
   * hacía que el segundo aterrizara como "factura_compra (1).pdf" y el usuario
   * abriera el primero creyendo que era el que pidió. Se guarda con el nombre
   * propio del archivo —el mismo que muestra la lista— y la etiqueta queda solo
   * como respaldo por si la url no diera nombre.
   */
  onDownloadRequested(fileUrl: string, etiquetaSeccion: string): void {
    this.apiLeaseService.downloadDocumentByUrl(fileUrl).subscribe({
      next: (blob) => {
        if (!blob || blob.size === 0) {
          this.messageService.add({
            severity: 'error',
            summary: 'Error',
            detail: 'El archivo descargado está vacío. Intente de nuevo.'
          });
          return;
        }

        guardarBlobComo(blob, conExtensionDe(nombreLegibleDeUrl(fileUrl) || etiquetaSeccion, fileUrl));

        this.messageService.add({
          severity: 'success',
          summary: 'Descarga',
          detail: 'Archivo descargado correctamente.'
        });
      },
      error: (error) => {
        console.error('Error al descargar archivo:', error);
        this.messageService.add({
          severity: 'error',
          summary: 'Error',
          detail: 'No se pudo descargar el archivo.'
        });
      }
    });
  }

  /**
   * Persiste el sub-doc `contract` (PUT /leases/:id) y notifica.
   */
  private saveContract(contractData: any, successDetail: string): void {
    this.apiLeaseService.putCollection(contractData, undefined, 'leases/' + this.leaseDetailService.id)
      .subscribe((response) => {
        if (!response.error) {
          this.leaseDetailService.getLeaseDetail();
          this.messageService.add({
            severity: 'success',
            summary: 'Éxito',
            detail: successDetail
          });
        } else {
          this.messageService.add({
            severity: 'error',
            summary: 'Error',
            detail: response.msg || 'No se pudo guardar.'
          });
        }
      });
  }

  saveMoratoryRate(): void {
    if (this.rateForm.invalid) return;
    const { ratePercent, reason } = this.rateForm.value;
    this.savingRate = true;
    //* El contrato del backend va en decimal; aquí se captura en % porque es como se habla del tema.
    this.apiLeaseService.patchMoratoryRate(this.leaseDetailService.id, { rate: Number((ratePercent / 100).toFixed(4)), reason: reason.trim() }).subscribe((r) => {
      this.savingRate = false;
      if (r.error) {
        this.messageService.add({ severity: 'error', summary: 'No se cambió la tasa', detail: r.msg || 'La tasa debe estar entre 0 y 20 %.', life: 8000 });
        return;
      }
      this.rateForm.patchValue({ reason: '' });
      const cambio = r.data!;
      this.messageService.add({ severity: 'success', summary: 'Tasa actualizada', detail: `${(cambio.previous * 100).toFixed(2)} % → ${(cambio.moratoryRate * 100).toFixed(2)} %. Quedó en la Bitácora.`, life: 6000 });
      //* Contrato y Bitácora leen de `data`: se recarga el detalle, igual que al guardar el número de contrato.
      this.leaseDetailService.getLeaseDetail();
    });
  }

}
